import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Icon } from '../components/Icon'
import { Counter, KV, Legend } from '../components/misc'
import { ProgressBar, ProgressDonut } from '../components/progress'
import { AggStatusBadge } from '../components/badges'
import { PresenceDot } from '../components/PresenceDot'
import { ServiceCard } from './ServiceCard'
import { JourneyTicketPrint } from './JourneyTicketPrint'
import { Modal } from '../components/Modal'
import { useTerms } from '../i18n/TermsContext'
import { useReference } from '../data/ReferenceContext'
import {
  counters, groupByLevel, resolveEligibility,
  isResolved, isInProgress, isPending, isActiveJourney, journeyStatus,
} from '../domain/engine'
import type { Aggregator, CustomerDetails, LevelGroup, PresenceState } from '../domain/types'
import type { AppActions } from '../store/types'
import { env } from '../config/env'
import { setTags } from '../instrument'
import { isValidEmail, maskPhone } from '../lib/fieldValidation'

function truncateAggId(id: string): string {
  if (id.length <= 12) {return id}
  return id.slice(0, 4) + '…' + id.slice(-8)
}

function formatCreatedAt(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {return value}
  return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} · ${value}`
}

export function DetalhesScreen({ agg, orientation, blockStyle, onBack, onCancelJourney, actions, presence, locationName, getCustomer, updateCustomer }: {
  agg: Aggregator
  orientation: string
  blockStyle: string
  /** Nome real da unidade (falso 'Centro' hardcode removido — audit lista #7). */
  locationName?: string
  onBack: () => void
  onCancelJourney?: (aggregatorId: string) => void
  actions: AppActions
  presence: PresenceState
  getCustomer?: (customerId: number) => Promise<CustomerDetails>
  updateCustomer?: (customer: CustomerDetails) => Promise<CustomerDetails>
}) {
  const terms = useTerms()
  const { presetLabel, journeyFields } = useReference()
  const tickets = agg.tickets
  const currentJourneyStatus = journeyStatus(agg.status, tickets)
  const valuedCustomFields = (agg.customFields ?? []).filter((field) => field.currentValue != null && field.currentValue !== '')
  const valuedCustomFieldKeys = new Set(valuedCustomFields.map((field) => field.key))
  const c = counters(tickets)
  const levels = groupByLevel(tickets)
  const disponiveis = tickets.filter((t) => isPending(t.status) && resolveEligibility(tickets, t).podeIniciar)
  const refreshing = actions.isBulkActionPending?.('refresh') ?? false
  const canceling = actions.isJourneyActionPending?.(agg.aggregatorId, 'cancel') ?? false
  const [expandedLevels, setExpandedLevels] = useState<Set<number>>(new Set())
  const [editingCustomer, setEditingCustomer] = useState(false)
  const [customerDraft, setCustomerDraft] = useState<CustomerDetails | null>(null)
  const [savedCustomer, setSavedCustomer] = useState<CustomerDetails | null>(null)
  const [loadingCustomer, setLoadingCustomer] = useState(false)
  const [savingCustomer, setSavingCustomer] = useState(false)
  const [customerSaveError, setCustomerSaveError] = useState<string | null>(null)
  const customerEmailInvalid = customerDraft ? !isValidEmail(customerDraft.email) : false
  const visibleCustomer = savedCustomer?.id === agg.cliente.id ? savedCustomer : agg.cliente

  useEffect(() => {
    let alive = true
    if (!editingCustomer) {return}
    setCustomerDraft(null)
    setCustomerSaveError(null)
    if (!getCustomer) {
      setCustomerSaveError('Edição de customer indisponível nesta tela.')
      return
    }
    setLoadingCustomer(true)
    getCustomer(agg.cliente.id)
      .then((customer) => { if (alive) {setCustomerDraft(customer)} })
      .catch((error) => {
        if (alive) {setCustomerSaveError(error instanceof Error ? error.message : 'Não foi possível carregar o customer.')}
      })
      .finally(() => { if (alive) {setLoadingCustomer(false)} })
    return () => { alive = false }
  }, [agg.cliente.id, editingCustomer, getCustomer])

  const PARALLEL_COLLAPSE_THRESHOLD = 4
  const [pulsing, setPulsing] = useState(false)
  const prevTicketSnapshotRef = useRef(agg.tickets.map((t) => `${t.ticketId}:${t.status}`).join(','))
  // B1 (bug "2 bilhetes"): o portal do bilhete da jornada só monta no DOM
  // durante o print de fato. Antes ficava sempre montado — quando o operador
  // abria o TicketDetailModal e imprimia o bilhete individual, o CSS de
  // `body.fz-ticket-printing` deixa QUALQUER `.print-only` visível, então o
  // portal da jornada (ainda no body) saía junto, imprimindo 2 páginas.
  const [printingJourney, setPrintingJourney] = useState(false)

  useEffect(() => {
    const snapshot = agg.tickets.map((t) => `${t.ticketId}:${t.status}`).join(',')
    if (prevTicketSnapshotRef.current !== snapshot) {
      setPulsing(true)
      const t = setTimeout(() => setPulsing(false), 600)
      prevTicketSnapshotRef.current = snapshot
      return () => clearTimeout(t)
    }
  }, [agg.tickets])

  // Refs do ciclo de vida do print — guardam o timer de fallback e o handler
  // de `afterprint` do print atual pra poder cancelar antes de armar um novo
  // quando o operador dispara prints em sequência rápida (B6 da auditoria).
  // Sem isso, o `setTimeout(cleanup, 1500)` do print #1 dispara durante o
  // print #2 e remove a classe `fz-journey-printing` enquanto o segundo
  // bilhete está sendo impresso.
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const afterprintHandlerRef = useRef<(() => void) | null>(null)

  // Cleanup defensivo no unmount: se o operador fechar a aba durante o print
  // dialog, o afterprint nunca dispara e a classe fz-journey-printing fica
  // no body pra sempre, contaminando o próximo render. Garante remoção no
  // unmount e cancela qualquer timer/listener pendurado do print em curso.
  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = null
      }
      if (afterprintHandlerRef.current) {
        window.removeEventListener('afterprint', afterprintHandlerRef.current)
        afterprintHandlerRef.current = null
      }
      document.body.classList.remove('fz-journey-printing')
      setPrintingJourney(false)
    }
  }, [])

  // Esconde o chat widget do Tawk.to durante o print dialog. O legado
  // (filazero-app) faz o mesmo no printTicket() porque o widget aparece
  // no canto da tela e ia sair no meio do bilhete impresso.
  useEffect(() => {
    const tawk = document.querySelector<HTMLElement>('[title="chat widget"]')?.closest('div')
    if (!tawk) {return}
    const beforePrint = () => { tawk.style.display = 'none' }
    const afterPrint = () => { tawk.style.display = '' }
    window.addEventListener('beforeprint', beforePrint)
    window.addEventListener('afterprint', afterPrint)
    return () => {
      window.removeEventListener('beforeprint', beforePrint)
      window.removeEventListener('afterprint', afterPrint)
      tawk.style.display = ''
    }
  }, [])

  const handlePrintJourney = () => {
    if (tickets.length === 0) {return}
    actions.printJourney(agg.aggregatorId)

    // B6: cancela timer + listener do print anterior ANTES de armar o novo.
    // Sem isso, prints concorrentes em sequência rápida deixam o fallback
    // do print #1 armado durante o print #2 → cleanup dispara no meio da
    // impressão do segundo bilhete e a classe some.
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current)
      fallbackTimerRef.current = null
    }
    if (afterprintHandlerRef.current) {
      window.removeEventListener('afterprint', afterprintHandlerRef.current)
      afterprintHandlerRef.current = null
    }

    const cleanup = () => {
      document.body.classList.remove('fz-journey-printing')
      setPrintingJourney(false)
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = null
      }
      if (afterprintHandlerRef.current) {
        window.removeEventListener('afterprint', afterprintHandlerRef.current)
        afterprintHandlerRef.current = null
      }
    }
    document.body.classList.add('fz-journey-printing')

    const afterPrint = () => {
      cleanup()
    }
    window.addEventListener('afterprint', afterPrint, { once: true })
    afterprintHandlerRef.current = afterPrint

    // B1: monta o portal do bilhete ANTES de window.print() e via flushSync
    // — precisa estar no DOM (com o CSS .print-only já resolvido) no momento
    // síncrono em que o browser tira o snapshot pra impressão. setState
    // normal enfileira o render pro próximo tick, tarde demais para
    // window.print() (que é síncrono/bloqueante). Uso pontual e justificado;
    // não usar flushSync fora deste caso.
    // eslint-disable-next-line @eslint-react/dom-no-flush-sync
    flushSync(() => setPrintingJourney(true))

    window.print()
    // Fallback de segurança: se o browser NÃO disparar `afterprint` (cancelar
    // diálogo, fechar aba, falha do print pipeline), remove a classe após
    // 1.5s para não contaminar o próximo render. Não usar setTimeout(0):
    // isso sempre executa antes do `afterprint` e removeria a classe na mesma
    // tick, deixando o bilhete invisível durante a impressão.
    //
    // B7: gate por build mode (vite injeta `import.meta.env.MODE`), não por
    // flag mutável da `window`. A flag anterior `__fzE2E` podia ser setada
    // por qualquer extensão Chrome / devtool / A-B test do SaaS do cliente
    // em prod, o que desabilitaria o fallback → bilhete fica invisível.
    // Em test mode, vitest roda com `mode: 'development'` (vide
    // vitest.config.ts); usamos `NODE_ENV` explícito no gate pra casar com
    // o ambiente real do vitest (`test`) e do jsdom (Playwright stub).
    const isTestMode = import.meta.env.MODE === 'test'
    if (!isTestMode) {
      fallbackTimerRef.current = setTimeout(cleanup, 1500)
    }
  }

  // Sentry tags for this screen — clear on unmount/navigation to avoid stale tags
  // between visits (e.g. Detalhes A → Operador → Detalhes A reusing aggregatorId).
  useEffect(() => {
    setTags({
      screen: 'DetalhesScreen',
      aggregatorId: agg.aggregatorId,
      orientation,
      blockStyle,
      ticketCount: String(agg.tickets.length),
    })
    return () => {
      setTags({
        screen: 'DetalhesScreen',
        aggregatorId: null,
        orientation: null,
        blockStyle: null,
        ticketCount: null,
      })
    }
  }, [agg.aggregatorId, orientation, blockStyle, agg.tickets.length, setTags])
  const initials = visibleCustomer.nome.split(' ').slice(0, 2).map((w) => w[0]).join('')
  const levelState = (lvl: LevelGroup) => {
    if (lvl.tickets.every((t) => isResolved(t.status))) {return 'is-done'}
    if (lvl.tickets.some((t) => isInProgress(t.status))) {return 'is-active'}
    if (lvl.tickets.some((t) => resolveEligibility(tickets, t).podeIniciar)) {return 'is-next'}
    return ''
  }

  const renderNextAvailable = () => {
    const blocked = tickets.filter((t) => {
      const elig = resolveEligibility(tickets, t)
      return !elig.podeIniciar && (t.status === 'Opened' || t.status === 'Authorized' || t.status === 'Confirmed' || t.status === 'Checked' || t.status === 'Reopened')
    })
    const b = blocked[0]
    const bElig = b ? resolveEligibility(tickets, b) : null
    const blockingTickets = bElig ? (bElig.bloqueadoPor ?? []).map((id) => tickets.find((x) => x.ticketId === id)).filter(Boolean) as typeof tickets : []

    return (
      <div className="fz-card fz-card--pad fz-next-card">
        <div className="fz-aside__title">Próximos disponíveis</div>
        <div className="fz-next">
          {disponiveis.length === 0 && <div className="fz-next__empty">Nenhum serviço liberado agora. {currentJourneyStatus === 'InProgress' ? 'Aguarde a etapa em curso terminar.' : 'Atendimento finalizado.'}</div>}
          {disponiveis.map((t) => (
            <div key={t.ticketId} className="fz-next__item fz-next__item--readonly">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fz-next__code">{t.codigoTicket} · {t.sala}</div>
                <div className="fz-next__name">{t.nomeServico}</div>
                {t.estMin > 0 && <div className="fz-next__eta">~{t.estMin} min estimados</div>}
              </div>
              <span className="fz-next__readonly-tag">Liberado</span>
            </div>
          ))}
          {b && bElig && (
            <div className="fz-next__blocked">
              <div className="fz-next__blocked-label">A liberar ({blocked.length})</div>
              <div className="fz-next__blocked-item">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fz-next__code">{b.codigoTicket} · {b.sala}</div>
                  <div className="fz-next__name">{b.nomeServico}</div>
                  {blockingTickets.length > 0 && (
                    <div className="fz-next__blocked-why">
                      Aguardando: {blockingTickets.map((bt) => `${bt!.codigoTicket} · ${bt!.nomeServico}`).join('; ')}
                    </div>
                  )}
                </div>
                <span className="fz-next__blocked-tag">Bloqueado</span>
              </div>
              {blocked.length > 1 && <div className="fz-next__blocked-more">+{blocked.length - 1} outros aguardando</div>}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={'fz-page' + (pulsing ? ' fz-page--pulse' : '')} data-screen-label="02 Detalhes do atendimento">
      <div className="fz-breadcrumb">
        <button onClick={onBack} style={{ minHeight: 44, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon name="arrowLeft" size={16} /> Atendimentos
        </button>
        <Icon name="chevR" size={14} />
        <span>{visibleCustomer.nome}</span>
      </div>

      <div className="fz-page__head">
        <div className="fz-patientcard">
          <div className="fz-patientcard__avatar">{initials}</div>
          <div>
            <h1 className="fz-patientcard__name">{visibleCustomer.nome}</h1>
            <div className="fz-patientcard__tags">
              {agg.metadata.templateKey && (
                <span className="fz-tag fz-tag--preset">{presetLabel(agg.metadata.templateKey)}</span>
              )}
            </div>
            <div className="fz-patientcard__badges">
              <AggStatusBadge tickets={tickets} journeyStatus={agg.status} />
              <PresenceDot state={presence} />
              {agg.prioridade > 0 && <span className="fz-prio">{agg.prioridade === 2 ? '+80' : 'Prioritário'}</span>}
            </div>
          </div>
        </div>
        <div className="fz-page__headactions">
          <div className="fz-page__headbtns">
            {isActiveJourney(agg.status, tickets) && <button
              type="button"
              className="fz-btn fz-btn--primary"
              onClick={() => actions.openJourneyEditor(agg.aggregatorId)}
            >
              <Icon name="plus" size={16} /> Editar jornada
            </button>}
            {isActiveJourney(agg.status, tickets) && onCancelJourney && <button
              type="button"
              className="fz-btn fz-btn--danger"
              onClick={() => onCancelJourney(agg.aggregatorId)}
              disabled={canceling}
              aria-busy={canceling}
            >
              {canceling ? <span className="fz-spin" /> : <Icon name="slash" size={16} />} {canceling ? 'Cancelando…' : 'Cancelar jornada'}
            </button>}
            <button
              type="button"
              className="fz-btn fz-btn--ghost"
              onClick={handlePrintJourney}
              disabled={tickets.length === 0}
              aria-label={`Imprimir senha da jornada ${agg.tickets[0]?.codigoTicket ?? ''}`}
              title={tickets.length === 0 ? 'Jornada sem tickets' : 'Imprimir senha da jornada'}
            >
              <Icon name="print" size={16} /> Imprimir senha
            </button>
            <button className="fz-btn fz-btn--neutral" disabled={refreshing} aria-busy={refreshing} onClick={() => actions.refresh()}>{refreshing ? <span className="fz-spin" /> : <Icon name="refresh" size={16} />} {refreshing ? 'Atualizando…' : 'Atualizar'}</button>
          </div>
          <div className="fz-page__headhint">
            Visualização do atendimento · ações ficam no painel do operador
          </div>
        </div>
      </div>

      <div className="fz-detail">
        <div className="fz-detail__main">
          <div className="fz-card fz-card--pad fz-detail__prog">
            <ProgressDonut tickets={tickets} />
            <div className="fz-detail__counters">
              <div className="fz-detail__counterrow">
                <Counter n={c.total} label="Serviços" />
                <Counter n={c.aguardando} label="Aguardando" />
                <Counter n={c.emAtendimento} label="Em atendimento" tone="active" />
                <Counter n={c.completos} label="Concluídos" tone="success" />
                {c.cancelados > 0 && <Counter n={c.cancelados} label="Cancelados" tone="danger" />}
              </div>
              <div className="fz-detail__progbar"><ProgressBar tickets={tickets} showLabel={false} /></div>
            </div>
          </div>

          <div className="fz-show-mobile-tablet" style={{ marginBottom: 22 }}>
            {renderNextAvailable()}
          </div>

          <div className="fz-section__label" style={{ marginBottom: 10 }}>Fluxo do atendimento</div>
          <Legend id="detalhe-fluxo">
            As etapas correm em sequência; serviços na mesma etapa são <b>paralelos</b>. Cada etapa libera a próxima ao concluir.
          </Legend>
          <div className={'fz-tl fz-tl--' + orientation}>
            {levels.map((lvl) => {
              const parallel = lvl.tickets.length > 1
              const svcName = parallel ? null : lvl.tickets[0].nomeServico
              return (
                <div key={lvl.sequence} className="fz-tl__level">
                  <span className="fz-tl__spine" />
                  <span className={'fz-tl__node ' + levelState(lvl)}>{lvl.sequence + 1}</span>
                  <div className="fz-tl__levelhead">
                    <div>
                      <span className="fz-tl__levelname">Etapa {lvl.sequence + 1}{svcName ? ' · ' + svcName : ''}</span>
                      {parallel && <span className="fz-tl__parallel"><Icon name="parallel" size={12} /> {lvl.tickets.length} em paralelo</span>}
                    </div>
                  </div>
                  <div className={[
                    'fz-tl__cards',
                    parallel ? 'is-parallel' : '',
                    parallel && lvl.tickets.length >= PARALLEL_COLLAPSE_THRESHOLD ? 'is-grid' : '',
                  ].join(' ')}>
                    {lvl.tickets.map((t, i) => {
                      const isCollapsed = parallel && lvl.tickets.length >= PARALLEL_COLLAPSE_THRESHOLD && !expandedLevels.has(lvl.sequence) && i >= 3
                      if (isCollapsed) {return null}
                      return <ServiceCard key={`${t.ticketId}-${i}`} ticket={t} tickets={tickets} blockStyle={blockStyle} actions={actions} readOnly />
                    })}
                  </div>
                  {parallel && lvl.tickets.length >= PARALLEL_COLLAPSE_THRESHOLD && !expandedLevels.has(lvl.sequence) && (
                    <button
                      className="fz-btn fz-btn--ghost fz-btn--sm"
                      style={{ marginTop: 8 }}
                      onClick={() => setExpandedLevels((prev) => { const n = new Set(prev); n.add(lvl.sequence); return n })}
                    >
                      Ver mais {lvl.tickets.length - 3} serviços paralelos
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <aside className="fz-aside">
          <div className="fz-show-desktop" style={{ marginBottom: 24 }}>
            {renderNextAvailable()}
          </div>
          <div className="fz-card fz-card--pad">
            <div className="fz-aside__title">Dados do atendimento</div>
            <div className="fz-kv">
              <KV k="Identificador" v={
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{truncateAggId(agg.aggregatorId)}</span>
                  <button
                    className="fz-icontiny"
                    title={agg.aggregatorId}
                    onClick={() => navigator.clipboard.writeText(agg.aggregatorId).catch(() => {})}
                    aria-label="Copiar identificador"
                  >
                    <Icon name="link" size={13} />
                  </button>
                </span>
              } />
              {(() => {
                const baseUrl = env.trackerUrl
                const safeBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/'
                const targetKey = agg.tickets[0]?.accessKey || agg.tickets[0]?.codigoTicket || agg.aggregatorId
                const qrUrl = `${safeBaseUrl}${targetKey}`
                return <KV k="Acompanhamento" v={<a href={qrUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'var(--color-primary)' }}>Abrir Tracker</a>} />
              })()}
              <KV k={terms.subject} v={
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {visibleCustomer.nome}
                  <button className="fz-icontiny" title="Editar customer" aria-label="Editar customer" onClick={() => setEditingCustomer(true)}>
                    <Icon name="pencil" size={13} />
                  </button>
                </span>
              } />
              <KV k="Telefone" v={visibleCustomer.telefone} />
              {visibleCustomer.email && <KV k="E-mail" v={visibleCustomer.email} />}
              {visibleCustomer.customData && Object.entries(visibleCustomer.customData).map(([k, v]) => (
                <KV key={k} k={k} v={v} />
              ))}
              {locationName && <KV k="Unidade" v={locationName} />}
              <KV k="Criado em" v={formatCreatedAt(agg.criadoEm)} />
              {agg.metadata.templateKey && <KV k={terms.template} v={presetLabel(agg.metadata.templateKey)} />}
              {valuedCustomFields.map((field) => (
                <KV key={field.key} k={field.label} v={field.currentValue ?? ''} />
              ))}
              {journeyFields.map((f) => !valuedCustomFieldKeys.has(f.key) && agg.metadata.fields[f.key]
                ? <KV key={f.key} k={f.label} v={agg.metadata.fields[f.key]} />
                : null)}
              <KV k="Prioridade" v={agg.prioridade === 2 ? '+80' : agg.prioridade === 1 ? 'Prioritário' : 'Normal'} />
            </div>
          </div>
        </aside>
      </div>

      {editingCustomer && (
        <Modal
          open
          onClose={() => setEditingCustomer(false)}
          titleId="customer-edit-title"
          title="Editar customer"
          closeOnOverlay={!savingCustomer}
        >
          <div data-screen-label="Editar customer">
            <div className="fz-modal__icon"><Icon name="pencil" size={24} /></div>
            <div className="fz-modal__title" id="customer-edit-title">Editar customer</div>
            {loadingCustomer && <div className="fz-modal__text" role="status">Carregando dados do customer…</div>}

            {customerDraft && (
              <>
                <div className="fz-field">
                  <label className="fz-field__label" htmlFor="customer-edit-name">Nome</label>
                  <input id="customer-edit-name" className="fz-field__input" value={customerDraft.nome} autoFocus
                    onChange={(e) => setCustomerDraft((current) => current ? { ...current, nome: e.target.value } : current)} />
                </div>
                <div className="fz-field">
                  <label className="fz-field__label" htmlFor="customer-edit-phone">Telefone</label>
                  <input id="customer-edit-phone" className="fz-field__input" type="tel" inputMode="tel" autoComplete="tel"
                    value={maskPhone(customerDraft.telefone)}
                    onChange={(e) => setCustomerDraft((current) => current ? { ...current, telefone: maskPhone(e.target.value) } : current)} />
                </div>
                <div className="fz-field">
                  <label className="fz-field__label" htmlFor="customer-edit-email">E-mail</label>
                  <input id="customer-edit-email" className="fz-field__input" type="email" value={customerDraft.email}
                    autoComplete="email" aria-invalid={customerEmailInvalid} aria-describedby={customerEmailInvalid ? 'customer-edit-email-error' : undefined}
                    onChange={(e) => setCustomerDraft((current) => current ? { ...current, email: e.target.value } : current)} />
                  {customerEmailInvalid && <div id="customer-edit-email-error" className="fz-field__error">Informe um e-mail válido.</div>}
                </div>
              </>
            )}

            {customerSaveError && <div className="fz-field__error" role="alert">{customerSaveError}</div>}
            <div className="fz-modal__actions">
              <button className="fz-btn fz-btn--neutral fz-btn--block" onClick={() => setEditingCustomer(false)} disabled={savingCustomer}>Cancelar</button>
              <button className="fz-btn fz-btn--primary fz-btn--block" disabled={loadingCustomer || savingCustomer || customerEmailInvalid || !customerDraft?.nome.trim()} aria-busy={savingCustomer}
                onClick={async () => {
                  if (!customerDraft || !updateCustomer) {
                    setCustomerSaveError('Edição de customer indisponível nesta tela.')
                    return
                  }
                  setSavingCustomer(true)
                  setCustomerSaveError(null)
                  try {
                    const saved = await updateCustomer({
                      ...customerDraft,
                      nome: customerDraft.nome.trim(),
                      telefone: customerDraft.telefone.trim(),
                      email: customerDraft.email.trim(),
                    })
                    setSavedCustomer(saved)
                    setEditingCustomer(false)
                  } catch (e) {
                    // audit lista #2/#3: erro visível dentro do modal, não só toast global
                    setCustomerSaveError(e instanceof Error ? e.message : 'Não foi possível salvar o customer.')
                  } finally {
                    setSavingCustomer(false)
                  }
                }}>
                {savingCustomer ? <><span className="fz-spin" /> Salvando…</> : 'Salvar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bilhete de impressão da jornada inteira (só visível em @media print).
          B1: só monta no DOM durante o print de fato (ver `printingJourney`),
          evitando colidir com o portal do TicketDetailModal quando os dois
          ficam montados ao mesmo tempo (bug histórico "2 bilhetes"). */}
      <JourneyTicketPrint agg={agg} printing={printingJourney} />
    </div>
  )
}
